var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Clock } from "three";
import { Renderer } from "../../impl";
export class Runner {
    constructor() {
        this.tracks = [];
    }
    /**
     * Utility function that wraps a setup closure into a new Runner, then runs it to completion
     * @param desc
     * @param f
     */
    static run(desc, f) {
        return __awaiter(this, void 0, void 0, function* () {
            // Construct our renderer based on the provided description
            const renderer = yield Renderer.create(desc);
            desc.container.onresize = (ev) => {
                renderer.setSize({
                    x: desc.container.clientWidth,
                    y: desc.container.clientHeight,
                });
            };
            // Construct and set up our runner with our wrapped closure
            const runner = new Runner();
            yield runner.setup(() => f(renderer, renderer.getWorld().getCamera()));
            // Wait for the animation to complete
            yield new Promise((resolve) => {
                function animate() {
                    // Only re-queue the render loop if we still have animation work to do
                    const keepGoing = runner.animate();
                    if (keepGoing) {
                        requestAnimationFrame(animate);
                    }
                    // Render the frame
                    renderer.render();
                    // If we have stopped then resolve the promise to notify our waiters we're done
                    if (!keepGoing) {
                        resolve();
                    }
                }
                animate();
            });
            // Destroy the renderer now that we're done
            renderer.dispose();
        });
    }
    /**
     * Performs initial setup of the animation runner. The passed closure will set up the scene and yield the animation
     * tracks once all setup has been performed
     * @param f A closure that will yield the animation tracks after a setup phase.
     */
    setup(f) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.clock) {
                this.tracks = yield f();
                this.clock = new Clock();
            }
            else {
                throw new Error("'setup' on Runner instance called twice");
            }
        });
    }
    /**
     * Driver function that handles running all member animation tracks. This should be called *exactly* once every
     * frame that 'Runner' is active.
     *
     * Will return 'true' until all tracks have declared they are complete. Once this returns false then the 'Runner' is
     * complete. 'animate' can stop being called once the 'Runner' is complete.
     */
    animate() {
        let runnerShouldContinue = false;
        if (this.clock) {
            // Capture delta time just once for this frame. This keeps 'elapsedTime' consistent for all tracks rather
            // than 'elapsedTime' subtly shifting for each track as they are iterated through
            const deltaTime = this.clock.getDelta();
            for (const track of this.tracks) {
                const trackShouldContinue = track.animate(this.clock, deltaTime);
                // Flag the runner as incomplete if a single track is incomplete. It only takes one
                if (trackShouldContinue)
                    runnerShouldContinue = true;
            }
        }
        return runnerShouldContinue;
    }
}
